import React, { useEffect, useMemo, useState } from "react";
import { timeLogsAPI } from "../../api/client";
import { formatDuration } from "../../utils/formatDuration";
import { PERMISSIONS } from "../../constants/permissions";
import { usePermission } from "../../hooks/usePermission";

function monthKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export default function UserTimeLogsTab({ user }) {
  const canEdit = usePermission(PERMISSIONS.TIME_LOG_EDIT);
  const [month, setMonth] = useState(monthKey());
  const [projectId, setProjectId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({
    startedAt: "",
    endedAt: "",
    reason: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError("");
    try {
      const resp = await timeLogsAPI.userLogs(user.id, {
        month,
        ...(projectId ? { projectId } : {}),
      });
      setData(resp.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load time logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [user?.id, month, projectId]);

  const projectOptions = useMemo(() => {
    const groups = data?.groups || [];
    const map = new Map();
    groups.forEach((group) => {
      if (group.project?.id) {
        map.set(group.project.id, group.project.name);
      }
    });
    return Array.from(map.entries());
  }, [data]);

  if (loading) {
    return <div className="small-muted">Loading time logs...</div>;
  }

  return (
    <div>
      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="stage-assign-row">
          <select value={month} onChange={(e) => setMonth(e.target.value)}>
            <option value={monthKey()}>This Month</option>
          </select>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">All Projects</option>
            {projectOptions.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="panel">
        <div className="stage-assign-row">
          <div>
            <div className="small-muted">Total Hours</div>
            <strong>{formatDuration(data?.totalMinutes || 0)}</strong>
          </div>
          <div>
            <div className="small-muted">Total time logged till now</div>
            <strong>{formatDuration(data?.totalMinutes || 0)}</strong>
          </div>
          {data?.additionalWorkMinutes > 0 && (
            <div>
              <div className="small-muted">Additional Work</div>
              <strong>{formatDuration(data.additionalWorkMinutes)}</strong>
            </div>
          )}
          <div>
            <div className="small-muted">Stages Completed</div>
            <strong>{data?.stagesCompleted || 0}</strong>
          </div>
          <div>
            <div className="small-muted">Active Right Now</div>
            <strong>-</strong>
          </div>
        </div>
      </div>

      <div className="panel" style={{ marginTop: 12 }}>
        {(data?.groups || []).length === 0 && (
          <div className="small-muted">No time logged yet this month.</div>
        )}
        {(data?.groups || []).map((group) => (
          <div key={group.stageAssignmentId} style={{ marginBottom: 12 }}>
            <div
              className="stage-assign-row"
              style={{ justifyContent: "space-between" }}
            >
              <div>
                <div style={{ fontWeight: 600 }}>{group.stageName}</div>
                <div className="small-muted">{group.project?.name}</div>
              </div>
              <div className="small-muted">
                {formatDuration(group.totalMinutes)} ·{" "}
                {(group.sessions || []).length} sessions
              </div>
              <button
                className="btn-secondary"
                onClick={() =>
                  setExpanded((prev) => ({
                    ...prev,
                    [group.stageAssignmentId]: !prev[group.stageAssignmentId],
                  }))
                }
              >
                {expanded[group.stageAssignmentId] ? "Hide" : "View"} Sessions
              </button>
            </div>
            {expanded[group.stageAssignmentId] && (
              <div style={{ marginTop: 8 }}>
                {(group.sessions || []).map((session, index) => (
                  <div key={session.id} className="small-muted">
                    Session {index + 1}:{" "}
                    {new Date(session.startedAt).toLocaleTimeString()} →{" "}
                    {session.endedAt
                      ? new Date(session.endedAt).toLocaleTimeString()
                      : "Active"}{" "}
                    = {formatDuration(session.durationMinutes || 0)}
                    {session.isAdditionalWork && (
                      <span style={{ marginLeft: 8, fontWeight: 600 }}>
                        Additional work: {session.reason}
                      </span>
                    )}
                    {canEdit && (
                      <button
                        className="btn-secondary"
                        style={{ marginLeft: 8 }}
                        onClick={() => {
                          setEditing(session);
                          setEditForm({
                            startedAt: new Date(session.startedAt)
                              .toISOString()
                              .slice(0, 16),
                            endedAt: session.endedAt
                              ? new Date(session.endedAt)
                                  .toISOString()
                                  .slice(0, 16)
                              : "",
                            reason: "",
                          });
                        }}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {error && (
          <div className="small-muted" style={{ color: "#b91c1c" }}>
            {error}
          </div>
        )}
      </div>

      {editing && (
        <div className="um-dialog-overlay" role="dialog" aria-modal="true">
          <div className="um-dialog um-modal-content">
            <div className="um-dialog-head">
              <h2>Correct this time entry</h2>
              <button
                className="um-close-btn"
                type="button"
                onClick={() => setEditing(null)}
                aria-label="Close"
              >
                x
              </button>
            </div>
            <div className="um-dialog-body">
              <label className="stage-label">Start time</label>
              <input
                type="datetime-local"
                value={editForm.startedAt}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    startedAt: e.target.value,
                  }))
                }
              />
              <label className="stage-label" style={{ marginTop: 8 }}>
                End time
              </label>
              <input
                type="datetime-local"
                value={editForm.endedAt}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, endedAt: e.target.value }))
                }
              />
              <label className="stage-label" style={{ marginTop: 8 }}>
                Reason for correction
              </label>
              <input
                type="text"
                value={editForm.reason}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, reason: e.target.value }))
                }
              />
            </div>
            <div className="um-dialog-footer">
              <button
                className="um-btn um-btn-primary"
                disabled={saving || !editForm.reason}
                onClick={async () => {
                  setSaving(true);
                  try {
                    await timeLogsAPI.edit(editing.id, {
                      startedAt: editForm.startedAt,
                      endedAt: editForm.endedAt,
                      reason: editForm.reason,
                    });
                    setEditing(null);
                    await load();
                  } catch (err) {
                    setError(
                      err.response?.data?.message || "Failed to edit time log",
                    );
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Save
              </button>
              <button
                className="um-btn um-btn-secondary"
                onClick={() => setEditing(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}





